import { withErrorBoundary } from 'app/components/error-boundary';
import { RatingsSection } from 'app/components/rating-card';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Page, type PageScrollViewRef } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useRatings } from 'app/network/loan';
import { useUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import React from 'react';
import { useTranslation } from 'react-i18next';

const RatingsScreenComponent: NavigationScreen<'ratings'> = ({ navigation, route }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { userId, listingId, subjectName, listingTitle, scrollToLoanId } = route.params;
  const { data: user } = useUser();
  const pageScrollRef = React.useRef<PageScrollViewRef | null>(null);

  const ratingsQuery = useRatings({ userId, listingId });
  const ownerName = subjectName ?? t('reviews.defaultSubject', { ns: 'neighbour-profile' });
  const resolvedListing = listingId ? ratingsQuery.data?.find((rating) => rating.listingTitle) : undefined;
  const resolvedListingTitle = listingTitle ?? (listingId ? resolvedListing?.listingTitle : undefined);

  useNavigationOptions(navigation, {
    headerTitle: () =>
      listingId && resolvedListingTitle ? (
        <Row gap="spacing.general.x3">
          {resolvedListing?.listingPicture ? (
            <PCImage
              height={34}
              width={34}
              source={{ uri: getImageVariantUrl(resolvedListing.listingPicture, 'mini') }}
              style={{ borderRadius: 17 }}
              useOwnCache
            />
          ) : null}
          <Column flex={1}>
            <Text color="white-label.header.on-header" fontWeight="700" numberOfLines={1} variant="bodyBase">
              {resolvedListingTitle}
            </Text>
            <Text color="white-label.header.on-header" numberOfLines={1} style={{ opacity: 0.8 }} variant="bodySmall">
              {ownerName}
            </Text>
          </Column>
        </Row>
      ) : (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('tabs.reviews', { ns: 'neighbour-profile' })}
        </Text>
      ),
  });

  if (ratingsQuery.isPending) {
    return (
      <Page>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: theme.spacing['spacing.general.x12'] }}
        >
          <Text color="text.secondary" variant="bodyBase">
            {t('reviews.loading', { ns: 'posts' })}
          </Text>
        </Column>
      </Page>
    );
  }

  if (ratingsQuery.isError) {
    return (
      <Page>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: theme.spacing['spacing.general.x12'] }}
        >
          <Text color="surface.error.default" textAlign="center" variant="bodyBase">
            {ratingsQuery.error.message}
          </Text>
        </Column>
      </Page>
    );
  }

  return (
    <Page
      pageScrollRef={pageScrollRef}
      paddingStyle={{ paddingHorizontal: theme.spacing['spacing.general.x4'] }}
      showsVerticalScrollIndicator={false}
    >
      <RatingsSection
        ratings={ratingsQuery.data}
        subjectName={ownerName}
        onPressListing={listingId ? undefined : (id) => navigation.navigate('one-listing', { id }, { pop: true })}
        currentUserId={user?.id}
        ratedUserId={userId}
        ratedListingId={listingId}
        parentScrollRef={pageScrollRef}
        scrollToLoanId={scrollToLoanId}
        variant={{
          listing: listingId ? 'hidden' : 'visible',
          padding: 'without-padding',
        }}
        onEditRating={(rating) => {
          navigation.navigate(
            'update-rating',
            {
              loanId: rating.loanId,
              listingId: rating.listingId,
              channelUrl: rating.channelUrl,
              isLender: rating.reviewerRole === 'lender',
              subjectName:
                [rating.ratedUserFirstName, rating.ratedUserLastName].filter(Boolean).join(' ').trim() ||
                rating.listingTitle ||
                '',
              subjectImage: rating.reviewerPicture ?? null,
              overallRating: rating.overallRating,
              assetQualityRating: rating.assetQualityRating,
              logisticsReliabilityRating: rating.logisticsReliabilityRating,
              communityTrustRating: rating.communityTrustRating,
              comment: rating.comment,
              initialExperience: (rating.overallRating ?? 0) >= 3 ? 'positive' : 'negative',
            },
            { pop: true },
          );
        }}
        onNavigateToSupport={() =>
          navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
            i18nKey: 'reportRatingPrompt',
            ns: 'neighbour-profile',
          })
        }
      />
    </Page>
  );
};

export const RatingsScreen: NavigationScreen<'ratings'> = withErrorBoundary(RatingsScreenComponent);
